import {
  Box,
  createTheme,
  ThemeProvider,
  Slide,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { format, startOfMonth, endOfMonth } from "date-fns";
import StatusAlert from "../../../StatusAlertList/StatusAlert";
import { DriversSalary, clientApis } from "../../../Data/axios";
import config from "../../../Data/AuthUser";
import HeaderDriverSalary from "./HeaderDriverSalary";
import DriverSalaryTransactions from "./DriverSalaryTransactions";
import DriverDepositTransactions from "./DriverDepositTransactions";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function DriverSalary() {
  // View State
  const [currentView, setCurrentView] = useState("salary"); // 'salary' or 'transactions'

  // State for Filters
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState(startOfMonth(new Date()));
  const [toDate, setToDate] = useState(endOfMonth(new Date()));

  // State for Data
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for Alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Fetch Clients for Dropdown
  const fetchClients = async (query = "") => {
    try {
      let response = await clientApis.getAll(1, 50, query ? "company_name" : undefined, query);
      if (response && response.data) {
        if (response.data.length === 0 && query) {
          response = await clientApis.getAll(1, 50, "username", query);
        }
        if (response && response.data) {
          setClients(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      showAlert("Failed to fetch clients", "error");
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchClients(clientSearchQuery);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [clientSearchQuery]);

  // Fetch Salary/Attendance Data
  const handleLoadSalary = async () => {
    if (!clientId) {
      showAlert("Please select a client first", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      showAlert("Please select both From and To dates", "warning");
      return;
    }

    setLoading(true);
    try {
      const formattedFrom = format(fromDate, "dd/MM/yyyy");
      const formattedTo = format(toDate, "dd/MM/yyyy");

      const response = await DriversSalary.getDriverAttendanceRecordForDB(
        clientId,
        {
          from_date: formattedFrom,
          to_date: formattedTo,
        }
      );

      if (response.success) {
        let data = response.data || [];
        // Initialize editable fields if missing or just map them
        // We will map them to our internal structure
        const processed = data.map(processRowData);
        setAttendanceData(processed);

        if (data.length === 0) {
          showAlert("No records found for the selected criteria", "info");
        } else {
          showAlert("Data loaded successfully", "success");
        }
      } else {
        setAttendanceData([]);
        showAlert(response.message || "Failed to load data", "error");
      }
    } catch (error) {
      console.error("Error fetching salary data:", error);
      showAlert("An error occurred while fetching data", "error");
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (attendanceData.length === 0) {
      showAlert("No data to confirm", "warning");
      return;
    }

    setLoading(true);
    try {
      // Calculate totals
      const totals = attendanceData.reduce((acc, row) => {
        if (row.isTotalRow) return acc;
        acc.totalSalary += row.totalSalary || 0;
        acc.totalClaim += row.claim || 0;
        acc.totalAdvance += row.advance || 0;
        acc.totalBalance += row.balance || 0;
        acc.totalPresent += row.present || 0;
        return acc;
      }, { totalSalary: 0, totalClaim: 0, totalAdvance: 0, totalBalance: 0, totalPresent: 0 });

      // Drivers Array
      const drivers = attendanceData
        .filter(row => !row.isTotalRow)
        .map(row => {
          // Create formatted datetime string
          const now = new Date();
          const datetime = format(now, "yyyy-MM-dd HH:mm:ss");

          return {
            user_id: row.id,
            salary: row.totalSalary,
            time: datetime,
            status: 1,
            transfer_id: "N/A",
            file: null,
            note: `Claim: ${row.claim}, Advance: ${row.advance || 0}, Remarks: ${row.remark || 'N/A'}`,
            claim_amount: row.claim,           // NEW: Structured field
            advance_amount: row.advance || 0,  // NEW: Structured field
            remark_text: row.remark,            // NEW: Structured field
            deposit_days: row.depositDays || 0,
            deposit_amount: row.depositAmount || 0,
            from: format(fromDate, "yyyy-MM-dd"),
            till: format(toDate, "yyyy-MM-dd")
          };
        });

      // Items Array
      const items = [
        {
          item: `Driver Salary - ${format(fromDate, "MMMM yyyy")} to ${format(toDate, "dd/MM/yyyy")}`,
          description: "Monthly salary payment for drivers",
          quality_type: null,
          quantity: totals.totalPresent,
          unit_price: attendanceData[0]?.salaryPerDay || 0,
          tax: null,
          amount: totals.totalSalary,
          file: null
        }
      ];

      // Add claim item only if there are claims
      if (totals.totalClaim > 0) {
        items.push({
          item: "Additional Claims",
          description: attendanceData
            .filter(row => !row.isTotalRow && row.claim > 0)
            .map(row => `${row.name}: ${row.claim}`)
            .join(", "),
          quality_type: null,
          quantity: 0,
          unit_price: 0,
          tax: null,
          amount: totals.totalClaim,
          file: null
        });
      }

      const payload = {
        client_id: clientId,
        Invoice_date: format(new Date(), "yyyy-MM-dd"),
        total_amount: totals.totalSalary,
        currency: 1,
        status: 1,
        discount: totals.totalClaim,
        paid_amount: 0,
        note: "Monthly driver salary payment",
        terms: "Payment due within 15 days",
        drivers: drivers,
        items: items
      };

      console.log("Creating Invoice Payload:", payload);

      // Corrected API Call: DriversSalary instead of clientApis
      const response = await DriversSalary.createDriverSalaryInvoice(payload);

      if (response.success) {
        showAlert(`Invoice Created Successfully! ID: ${response.invoiceNumber || response.invoiceId}`, "success");
      } else {
        showAlert(`Failed to create invoice: ${response.message}`, "error");
      }

    } catch (error) {
      console.error("Error confirming salary:", error);
      showAlert("An error occurred during confirmation", "error");
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel (isAdmin=true includes the deposit/hold columns)
  const handleExport = (isAdmin = false) => {
    if (attendanceData.length === 0) {
      showAlert("No data to export", "warning");
      return;
    }

    // Use xlsx-js-style for styling support
    import("xlsx-js-style").then(XLSX => {
      const wb = XLSX.utils.book_new();

      // --- 1. Define Data & Structure ---

      // Get selected client name for dynamic title
      const selectedClient = clients.find(c => c.user_id === clientId);
      const clientName = (selectedClient?.company_name || selectedClient?.username || "Client").toUpperCase();

      const titleRow = [`ROAD PILOT TECHNOLOGIES PVT LTD + ${clientName}`];
      const subtitleRow = ["Attendance Tracking"];
      const dateRangeRow = [`${format(fromDate, "dd/MM/yyyy")} TO ${format(toDate, "dd/MM/yyyy")}`];
      const emptyRow = [];

      // Date Headers
      const dateHeaders = [];
      let currentDate = new Date(fromDate);
      while (currentDate <= toDate) {
        dateHeaders.push(format(currentDate, "dd-MMM"));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Table Headers
      const depositHeaders = isAdmin ? ["Deposit", "Deposit Days", "Remaining"] : [];
      const advanceHeaders = isAdmin ? ["Advance"] : [];
      const tableHeaders = [
        "", "Name", "PRESENT", "OFF DUTY", "Salary/day",
        ...dateHeaders,
        "Total salary", "Claim", ...advanceHeaders, "Balance",
        ...depositHeaders,
        "Remark"
      ];

      // Data Rows
      const dataRows = attendanceData.map(row => {
        if (row.isTotalRow) return null;

        const rowData = [
          "", // Empty
          row.name,
          "", // Present (Re-calculated or placeholder)
          "", // Off Duty
          row.salaryPerDay
        ];

        // Re-calculate daily P/A for export
        let current = new Date(fromDate);
        const dailyAttendance = [];
        let pCount = 0;
        let aCount = 0;

        while (current <= toDate) {
          const dateStr = format(current, "yyyy-MM-dd");
          const attendanceRecord = row.originalAttendance?.find(a => a.date === dateStr);
          const status = attendanceRecord?.status;

          // Only mark as 'A' (off duty) if there's an attendance record for this date
          // Days before driver joined (no attendance record) should be blank, not counted as off duty
          if (status === 1602) {
            dailyAttendance.push('P');
            pCount++;
          } else if (attendanceRecord) {
            // Only mark as 'A' if there's an attendance record and it's not present
            dailyAttendance.push('A');
            aCount++;
          } else {
            // No attendance record for this date (likely before joining date)
            // Leave blank and don't count as off duty
            dailyAttendance.push('');
          }
          current.setDate(current.getDate() + 1);
        }

        // Fill Calculated Present/Off Duty
        rowData[2] = pCount;
        rowData[3] = aCount;

        // Add Daily Columns
        rowData.push(...dailyAttendance);

        rowData.push(row.totalSalary);
        rowData.push(row.claim);
        if (isAdmin) rowData.push(row.advance || 0);
        // Normal sheet: balance excludes advance/deposit, only claims are deducted
        rowData.push(isAdmin ? row.balance : row.totalSalary - row.claim);
        if (isAdmin) {
          rowData.push(row.depositAmount);
          rowData.push(row.depositDays);
          rowData.push(row.remainingDepositDays);
        }
        // Normal sheet: remark keeps claim/notes but drops any Deposit: tail
        const remark = isAdmin || !row.remark ? row.remark : row.remark.replace(/,\s*Deposit:[^,\n]*/gi, '');
        rowData.push(remark);

        return rowData;
      }).filter(r => r !== null);

      // Totals Row
      // Calculate totals dynamically as attendanceData does not contain the total row
      const totals = attendanceData.reduce((acc, row) => {
        if (row.isTotalRow) return acc;
        acc.present += row.present || 0;
        acc.offDuty += row.offDuty || 0;
        acc.totalSalary += row.totalSalary || 0;
        acc.claim += row.claim || 0;
        acc.advance += row.advance || 0;
        acc.balance += row.balance || 0;
        acc.depositAmount += row.depositAmount || 0;
        acc.depositDays += row.depositDays || 0;
        return acc;
      }, { present: 0, offDuty: 0, totalSalary: 0, claim: 0, advance: 0, balance: 0, depositAmount: 0, depositDays: 0 });

      const depositTotals = isAdmin ? [totals.depositAmount, totals.depositDays, ""] : [];
      const advanceTotals = isAdmin ? [totals.advance] : [];
      const totalRow = [
        "", "TOTALS",
        totals.present,
        totals.offDuty,
        "",
        ...Array(dateHeaders.length).fill(""),
        totals.totalSalary,
        totals.claim,
        ...advanceTotals,
        isAdmin ? totals.balance : totals.totalSalary - totals.claim,
        ...depositTotals,
        ""
      ];

      const wsData = [
        titleRow,
        subtitleRow,
        dateRangeRow,
        emptyRow,
        tableHeaders,
        ...dataRows,
        totalRow
      ];

      // Create Sheet from Data
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // --- 2. Apply Styles ---
      // Helper for cell styling
      const setStyle = (cellRef, style) => {
        if (!ws[cellRef]) return;
        ws[cellRef].s = style;
      };

      const range = XLSX.utils.decode_range(ws['!ref']);

      // Define Styles
      const titleStyle = {
        font: { name: "Arial Black", sz: 16, bold: true },
        alignment: { horizontal: "center", vertical: "center" }
      };
      const headerStyle = {
        fill: { fgColor: { rgb: "4CAF50" } },
        font: { color: { rgb: "FFFFFF" }, bold: true },
        alignment: { horizontal: "center", vertical: "center" },
        border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
      };
      const pStyle = {
        fill: { fgColor: { rgb: "00B050" } },
        font: { color: { rgb: "FFFFFF" }, bold: true },
        alignment: { horizontal: "center" }
      };
      const aStyle = {
        fill: { fgColor: { rgb: "FFFF99" } },
        font: { color: { rgb: "000000" }, bold: true },
        alignment: { horizontal: "center" }
      };
      const claimStyle = {
        fill: { fgColor: { rgb: "FF0000" } },
        font: { color: { rgb: "FFFFFF" }, bold: true }
      };
      const totalRowStyle = {
        fill: { fgColor: { rgb: "E8E8E8" } },
        font: { bold: true },
        alignment: { horizontal: "center" }
      };

      // Apply Styles Iterate
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellRef]) continue;
          const cellVal = ws[cellRef].v;

          // Title Rows (0-2)
          if (R <= 2) {
            setStyle(cellRef, titleStyle);
            continue;
          }

          // Table Header (Row 4) (0-indexed) -> 5th row
          if (R === 4) {
            setStyle(cellRef, headerStyle);
            continue;
          }

          // Data Rows
          if (R > 4 && R < range.e.r) {
            // P/A check
            if (cellVal === 'P') setStyle(cellRef, pStyle);
            if (cellVal === 'A') setStyle(cellRef, aStyle);

            // Claim Column Check (Claim is at index: 4 (Fixed) + dateHeaders.length + 1 (Total) + 1 (Claim))
            // Table Cols: "", Name, Prest, Off, Sal, [Dates...], Total, Claim, Balance, Remark
            // Indices:     0    1     2      3    4    5..N        N+1    N+2    N+3     N+4
            const claimColIndex = 4 + dateHeaders.length + 2;
            if (C === claimColIndex && typeof cellVal === 'number' && cellVal > 0) {
              setStyle(cellRef, claimStyle);
            }
          }

          // Totals Row (Last Row)
          if (R === range.e.r) {
            setStyle(cellRef, totalRowStyle);
          }
        }
      }

      // --- 3. Merges & Widths ---

      // Merge Headers
      const endColIndex = tableHeaders.length - 1;
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: endColIndex } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: endColIndex } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: endColIndex } }
      ];

      // Column Widths
      const depositWidths = isAdmin ? [{ wch: 12 }, { wch: 12 }, { wch: 12 }] : [];
      const advanceWidths = isAdmin ? [{ wch: 10 }] : [];
      const wscols = [
        { wch: 5 },  // Empty
        { wch: 25 }, // Name
        { wch: 10 }, // Present
        { wch: 10 }, // Off Duty
        { wch: 10 }, // Salary
        ...dateHeaders.map(() => ({ wch: 8 })), // Dates
        { wch: 12 }, // Total
        { wch: 10 }, // Claim
        ...advanceWidths,
        { wch: 12 }, // Balance
        ...depositWidths,
        { wch: 20 }  // Remark
      ];
      ws['!cols'] = wscols;

      // Append & Write
      XLSX.utils.book_append_sheet(wb, ws, isAdmin ? "Attendance Report (Admin)" : "Attendance Report");
      XLSX.writeFile(wb, `Driver_Salary_${isAdmin ? "ADMIN_" : ""}${format(new Date(), "yyyyMMdd")}.xlsx`);
    }).catch(err => {
      console.error("Export Error:", err);
      showAlert("Failed to export. Ensure xlsx-js-style is installed.", "error");
    });
  };

  // Data Processing for Table
  const processRowData = (row) => {
    const presentDays = row.attendance?.filter(a => a.status === 1602).length || 0;
    const offDutyDays = row.attendance?.filter(a => a.status !== 1602).length || 0;

    const salaryPerDay = parseFloat(row.salary_per_day) || 0;
    const totalSalary = presentDays * salaryPerDay;

    // Use preserved claim/remark data from API response
    const claim = parseFloat(row.claim_amount) || 0;
    const advance = parseFloat(row.advance_amount) || 0;
    const remark = row.remark_text || "";

    // Deposit hold is decided by the backend: first assignment only, within 45 days
    // of first joining, and while lifetime present days stay under the cap.
    const held = parseInt(row.held_deposit_days);
    const rem = parseInt(row.remaining_deposit_days);
    const heldDepositDays = Number.isFinite(held) ? Math.max(0, held) : 0;
    const remainingDepositDays = Number.isFinite(rem) ? Math.max(0, rem) : 0;
    const depositEligible = row.deposit_eligible === true;
    const depositBlockReason = row.deposit_block_reason || null;
    const lifetimePresentDays = parseInt(row.lifetime_present_days) || 0;

    const autoDepositDays = depositEligible ? Math.min(presentDays, remainingDepositDays) : 0;
    const autoDepositAmount = autoDepositDays * salaryPerDay;
    const preservedDepositDays = parseInt(row.deposit_days) || 0;
    // Saved holds stay visible/saved; otherwise auto-calc only while eligible
    const depositDays = preservedDepositDays > 0 ? preservedDepositDays : autoDepositDays;
    const depositAmount = preservedDepositDays > 0
      ? (parseFloat(row.deposit_amount) || 0)
      : Math.round(autoDepositAmount * 100) / 100;

    // Net payable = gross - claim - advance - deposit hold
    const balance = totalSalary - claim - advance - depositAmount;

    return {
      id: row.driver_id,
      name: row.username || row.driver_id,
      image: row.image,
      present: presentDays,
      offDuty: offDutyDays,
      salaryPerDay: salaryPerDay,
      totalSalary: totalSalary,
      claim: claim,              // Preserved from previous data
      advance: advance,          // Preserved from previous data
      balance: balance,
      remark: remark,            // Preserved from previous data
      heldDepositDays: heldDepositDays,
      remainingDepositDays: remainingDepositDays,
      depositEligible: depositEligible,
      depositBlockReason: depositBlockReason,
      lifetimePresentDays: lifetimePresentDays,
      depositDays: depositDays,
      depositAmount: depositAmount,
      isTotalRow: false,
      originalAttendance: row.attendance || [], // Keep original for export
      previousInvoiceId: row.previous_invoice_id // For reference
    };
  };

  // Handle Edit for Claim and Remark
  const handleEditChange = (index, field, value) => {
    const newData = [...attendanceData];
    const row = newData[index];

    // Don't edit total row directly (it's calculated)
    if (row.isTotalRow) return;

    if (field === 'claim') {
      const claimVal = parseFloat(value) || 0;
      row.claim = claimVal;
      row.balance = row.totalSalary - claimVal - (row.advance || 0) - (row.depositAmount || 0);
    } else if (field === 'advance') {
      row.advance = Math.max(0, parseFloat(value) || 0);
      row.balance = row.totalSalary - (row.claim || 0) - row.advance - (row.depositAmount || 0);
    } else if (field === 'remark') {
      row.remark = value;
    } else if (field === 'deposit') {
      const amount = Math.max(0, parseFloat(value) || 0);
      row.depositAmount = amount;
      const maxDays = Math.max(0, Math.min(row.present || 0, row.depositEligible ? row.remainingDepositDays : 0));
      row.depositDays = Math.max(0, Math.min(Math.round(amount / (row.salaryPerDay || 1)), maxDays));
      row.balance = row.totalSalary - (row.claim || 0) - (row.advance || 0) - amount;
    } else if (field === 'depositDays') {
      const maxDays = Math.max(0, Math.min(row.present || 0, row.depositEligible ? row.remainingDepositDays : 0));
      const days = Math.max(0, Math.min(parseInt(value) || 0, maxDays));
      row.depositDays = days;
      row.depositAmount = Math.round(days * (row.salaryPerDay || 0) * 100) / 100;
      row.balance = row.totalSalary - (row.claim || 0) - (row.advance || 0) - row.depositAmount;
    }

    setAttendanceData(newData);
  };

  // Calculate Totals and prepare display data
  const getDisplayData = () => {
    if (attendanceData.length === 0) return [];

    const totalRow = attendanceData.reduce((acc, row) => {
      acc.present += row.present || 0;
      acc.offDuty += row.offDuty || 0;
      acc.totalSalary += row.totalSalary || 0;
      acc.claim += row.claim || 0;
      acc.advance += row.advance || 0;
      acc.balance += row.balance || 0;
      acc.depositAmount += row.depositAmount || 0;
      return acc;
    }, {
      id: 'total',
      name: 'Total',
      image: null,
      present: 0,
      offDuty: 0,
      salaryPerDay: 0, // No sum for rate
      totalSalary: 0,
      claim: 0,
      advance: 0,
      balance: 0,
      depositAmount: 0,
      remark: '',
      isTotalRow: true
    });

    return [...attendanceData, totalRow];
  };

  const displayData = getDisplayData();

  const columns = [
    {
      name: "name",
      label: "Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (!row) return value;

          if (row.isTotalRow) {
            return <Typography variant="h6" fontWeight="bold" color="black">TOTAL</Typography>;
          }

          const imageUrl = row.image && row.image !== 'non'
            ? `${config.apiImageUrl}${row.image}`
            : null;

          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "left" }}>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={value}
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    marginRight: "10px",
                    objectFit: "cover"
                  }}
                />
              )}
              <Typography variant="body2" fontWeight="bold" color="black">{value}</Typography>
            </div>
          );
        }
      }
    },
    {
      name: "present",
      label: "PRESENT",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          const isTotal = row?.isTotalRow;
          return (
            <span style={{
              fontWeight: 'bold',
              color: isTotal ? 'black' : 'green',
              fontSize: isTotal ? '1.1rem' : 'inherit'
            }}>
              {value}
            </span>
          );
        }
      }
    },
    {
      name: "offDuty",
      label: "OFF DUTY",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          const isTotal = row?.isTotalRow;
          return (
            <span style={{
              fontWeight: 'bold',
              color: isTotal ? 'black' : '#f57c00',
              fontSize: isTotal ? '1.1rem' : 'inherit'
            }}>
              {value}
            </span>
          );
        }
      }
    },
    {
      name: "salaryPerDay",
      label: "Salary/day",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (row?.isTotalRow) return ""; // No total for calculated rate
          return <span style={{ color: 'black' }}>{value.toFixed(2)}</span>;
        }
      }
    },
    {
      name: "totalSalary",
      label: "Total salary",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          const isTotal = row?.isTotalRow;
          return (
            <span style={{
              fontWeight: 'bold',
              color: 'black',
              fontSize: isTotal ? '1.1rem' : 'inherit'
            }}>
              {value.toFixed(2)}
            </span>
          );
        }
      }
    },
    {
      name: "claim",
      label: "Claim",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (row?.isTotalRow) {
            return (
              <span style={{ fontWeight: 'bold', color: 'black', fontSize: '1.1rem' }}>
                {value.toFixed(2)}
              </span>
            );
          }
          return (
            <TextField
              type="number"
              value={value}
              onChange={(e) => handleEditChange(tableMeta.rowIndex, 'claim', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true, style: { fontWeight: 'bold', color: 'black' } }}
              sx={{ width: '80px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
            />
          );
        }
      }
    },
    {
      name: "advance",
      label: "Advance",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (row?.isTotalRow) {
            return (
              <span style={{ fontWeight: 'bold', color: 'black', fontSize: '1.1rem' }}>
                {value.toFixed(2)}
              </span>
            );
          }
          return (
            <TextField
              type="number"
              value={value}
              onChange={(e) => handleEditChange(tableMeta.rowIndex, 'advance', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true, style: { fontWeight: 'bold', color: 'black' } }}
              sx={{ width: '80px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
            />
          );
        }
      }
    },
    {
      name: "deposit",
      label: "Deposit",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (row?.isTotalRow) {
            return row.depositAmount > 0 ? (
              <span style={{ fontWeight: 'bold', color: 'black', fontSize: '1.1rem' }}>
                {row.depositAmount.toFixed(2)}
              </span>
            ) : "";
          }
          if (!row.depositEligible) {
            const reasonText = {
              not_first_assignment: "Not first assignment — no deposit",
              past_45_days: "Over 45 days since first joining — no deposit",
              present_days_exceeded: "More than 30 present days worked — no deposit",
            }[row.depositBlockReason] || "Deposit not applicable";
            return (
              <div>
                <TextField
                  type="number"
                  value={row.depositAmount}
                  disabled
                  variant="standard"
                  InputProps={{ disableUnderline: true, style: { fontWeight: 'bold', color: 'gray' } }}
                  sx={{ width: '100px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
                />
                <Typography variant="caption" color="gray" sx={{ display: 'block', lineHeight: 1.2 }}>
                  {reasonText}
                </Typography>
              </div>
            );
          }
          if (row.remainingDepositDays <= 0) {
            return (
              <Typography variant="caption" fontWeight="bold" color="green">
                Deposit complete — 15 days already held
              </Typography>
            );
          }
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TextField
                  type="number"
                  inputProps={{ min: 0, max: Math.min(row.present || 0, row.remainingDepositDays), style: { fontWeight: 'bold', color: 'black' } }}
                  value={row.depositDays}
                  onChange={(e) => handleEditChange(tableMeta.rowIndex, 'depositDays', e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ width: '50px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
                />
                <span style={{ color: 'gray', fontSize: '0.8rem' }}>d</span>
                <TextField
                  type="number"
                  value={row.depositAmount}
                  onChange={(e) => handleEditChange(tableMeta.rowIndex, 'deposit', e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true, style: { fontWeight: 'bold', color: 'black' } }}
                  sx={{ width: '90px', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
                />
              </div>
              <Typography variant="caption" color="gray" sx={{ display: 'block', lineHeight: 1.2 }}>
                {Math.max(0, row.remainingDepositDays - (row.depositDays || 0))} days remaining
              </Typography>
              {row.depositAmount > (row.totalSalary - (row.claim || 0) - (row.advance || 0)) && (
                <Typography variant="caption" fontWeight="bold" color="red" sx={{ display: 'block', lineHeight: 1.2 }}>
                  More than balance ₹{(row.totalSalary - (row.claim || 0) - (row.advance || 0)).toFixed(2)}
                </Typography>
              )}
            </div>
          );
        }
      }
    },
    {
      name: "balance",
      label: "Balance",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          const isTotal = row?.isTotalRow;
          return (
            <span style={{
              fontWeight: 'bold',
              color: value < 0 ? 'red' : (isTotal ? 'black' : 'blue'),
              fontSize: isTotal ? '1.1rem' : 'inherit'
            }}>
              {value.toFixed(2)}
            </span>
          );
        }
      }
    },
    {
      name: "remark",
      label: "Remark",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = displayData[tableMeta.rowIndex];
          if (row?.isTotalRow) return "";

          return (
            <TextField
              type="text"
              value={value}
              placeholder="Add remark"
              onChange={(e) => handleEditChange(tableMeta.rowIndex, 'remark', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true, style: { color: 'black' } }}
              sx={{ width: '100%', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 5px', borderRadius: '4px' }}
            />
          );
        }
      }
    },
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    viewColumns: false,
    search: false,
    download: false,
    print: false,
    pagination: false,
    elevation: 0,
    responsive: "standard",
    textLabels: {
      body: {
        noMatch: loading ? "Loading..." : "No records found",
      }
    },
  };

  const getTheme = () =>
    createTheme({
      components: {
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: "#FBC02D",
              color: "black",
              fontWeight: "bold",
              textAlign: "center"
            },
            body: {
              textAlign: "center",
              color: "black",
            },
            root: {
              backgroundColor: "#FFF9C4",
            }
          }
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              backgroundColor: "#FBC02D",
              color: "black",
            },
            sortAction: {
              '& path': {
                color: "black"
              }
            }
          }
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              backgroundColor: "#FFF9C4",
            }
          }
        }
      },
    });

  // Handle Update Transactions Button Click
  const handleUpdateTransactions = () => {
    if (!clientId) {
      showAlert("Please select a client first", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      showAlert("Please select both From and To dates", "warning");
      return;
    }
    setCurrentView("transactions");
  };

  // Handle Back to Salary View
  const handleBackToSalary = () => {
    setCurrentView("salary");
  };

  // Handle Show Credit Note (Deposits) View
  const handleShowCreditNote = () => {
    if (!clientId) {
      showAlert("Please select a client first", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      showAlert("Please select both From and To dates", "warning");
      return;
    }
    setCurrentView("creditNote");
  };

  return (
    <>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />

      {currentView === "transactions" ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleBackToSalary}
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#f57c00" },
                textTransform: "none",
              }}
            >
              ← Back to Salary
            </Button>
          </Box>
          <DriverSalaryTransactions
            clientId={clientId}
            fromDate={fromDate}
            toDate={toDate}
          />
        </>
      ) : currentView === "creditNote" ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleBackToSalary}
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#f57c00" },
                textTransform: "none",
              }}
            >
              ← Back to Salary
            </Button>
          </Box>
          <DriverDepositTransactions
            clientId={clientId}
            fromDate={fromDate}
            toDate={toDate}
          />
        </>
      ) : (
        <>
          <HeaderDriverSalary
            clientId={clientId}
            setClientId={setClientId}
            clients={clients}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            onLoadSalary={handleLoadSalary}
            onConfirm={handleConfirm}
            onExport={() => handleExport(false)}
            onExportAdmin={() => handleExport(true)}
            onClientSearchChange={setClientSearchQuery}
            onUpdateTransactions={handleUpdateTransactions}
            showUpdateButton={true}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, mb: 1 }}>
            <Button
              variant="contained"
              onClick={handleShowCreditNote}
              disabled={loading}
              sx={{
                backgroundColor: "#9c27b0",
                "&:hover": { backgroundColor: "#7b1fa2" },
                textTransform: "none",
              }}
            >
              Credit Note (Deposits)
            </Button>
          </Box>

          <Box sx={{ p: 2 }}>
            <ThemeProvider theme={getTheme()}>
              <MUIDataTable
                title={
                  <Typography variant="h5" fontWeight="bold" color="black">
                    Attendance Report
                  </Typography>
                }
                data={displayData}
                columns={columns}
                options={options}
              />
            </ThemeProvider>
          </Box>
        </>
      )}
    </>
  );
}

export default DriverSalary;
